// Posição da barra flutuante (P0.4): lembra a última posição e sempre aparece inteira
// dentro de um monitor visível, no tamanho certo para a escala dele (100%, 150%, Retina).
// Coordenadas FÍSICAS (pixels reais), como o Tauri devolve para monitores e janelas.

export interface Monitor {
  name: string | null;
  /** área útil do monitor, em pixels físicos */
  x: number;
  y: number;
  width: number;
  height: number;
  scaleFactor: number;
}

export interface SavedPosition {
  x: number;
  y: number;
}

export interface Placement {
  x: number;
  y: number;
  /** tamanho físico da barra naquele monitor */
  width: number;
  height: number;
  monitor: Monitor;
}

/** Tamanho lógico da barra (~300 × 48). */
export const FLOAT_SIZE = { width: 300, height: 48 } as const;
/** Barra crescida durante a comemoração com meme e confetes (ASM-023). */
export const FLOAT_EXPANDED = { width: 300, height: 260 } as const;

type LogicalSize = { width: number; height: number };
const TOP_MARGIN = 24; // lógico

export function physicalSize(monitor: Monitor, size: LogicalSize = FLOAT_SIZE): { width: number; height: number } {
  return {
    width: Math.round(size.width * monitor.scaleFactor),
    height: Math.round(size.height * monitor.scaleFactor),
  };
}

function contains(m: Monitor, x: number, y: number): boolean {
  return x >= m.x && x < m.x + m.width && y >= m.y && y < m.y + m.height;
}

function clampInto(m: Monitor, x: number, y: number, logical: LogicalSize = FLOAT_SIZE): Placement {
  const size = physicalSize(m, logical);
  const cx = Math.min(Math.max(x, m.x), m.x + m.width - size.width);
  const cy = Math.min(Math.max(y, m.y), m.y + m.height - size.height);
  return { x: cx, y: cy, ...size, monitor: m };
}

/** Posição padrão: centralizada no topo do monitor principal. */
export function defaultPlacement(primary: Monitor): Placement {
  const size = physicalSize(primary);
  return {
    x: Math.round(primary.x + (primary.width - size.width) / 2),
    y: Math.round(primary.y + TOP_MARGIN * primary.scaleFactor),
    ...size,
    monitor: primary,
  };
}

/**
 * Onde abrir a barra. A posição salva vale se o canto dela estiver em algum monitor
 * (ajustada para caber inteira); se o monitor sumiu, volta para o padrão no principal.
 */
export function placeFloat(saved: SavedPosition | null, monitors: readonly Monitor[], primary?: Monitor): Placement {
  const main = primary ?? monitors[0];
  if (!main) throw new Error('nenhum monitor disponível');
  if (saved) {
    const m = monitors.find((mon) => contains(mon, saved.x, saved.y));
    if (m) return clampInto(m, saved.x, saved.y);
  }
  return defaultPlacement(main);
}

/**
 * Muda o tamanho da barra (crescer para a comemoração / voltar) mantendo o canto
 * superior esquerdo, mas sem sair do monitor onde ela está.
 */
export function resizeInPlace(pos: SavedPosition, logical: LogicalSize, monitors: readonly Monitor[]): Placement {
  const m = monitors.find((mon) => contains(mon, pos.x, pos.y)) ?? monitors[0];
  if (!m) throw new Error('nenhum monitor disponível');
  return clampInto(m, pos.x, pos.y, logical);
}

const STORAGE_KEY = 'beedoit.float.position';

export function loadSavedPosition(storage: Pick<Storage, 'getItem'>): SavedPosition | null {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return Number.isFinite(p?.x) && Number.isFinite(p?.y) ? { x: p.x, y: p.y } : null;
  } catch {
    return null;
  }
}

export function savePosition(storage: Pick<Storage, 'setItem'>, pos: SavedPosition): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ x: Math.round(pos.x), y: Math.round(pos.y) }));
  } catch {
    /* armazenamento indisponível: a barra só não lembra a posição */
  }
}
